import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Scale,
  Leaf,
  Info,
  Shield,
  ArrowRight,
  Calendar,
  Clock,
  FileText
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { WizardFlow } from '@/components/wizard/WizardFlow';
import { SEO, OrganizationStructuredData } from '@/components/SEO';
import { blogApi } from '@/services/api';
import { CategoryBrowser } from '@/components/CategoryBrowser';

// Latest Blog Post Preview Component
const LatestBlogPost = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestPost = async () => {
      try {
        const response = await blogApi.getPublished();
        const posts = response?.posts || response || [];
        if (posts && posts.length > 0) {
          // Sort by published date and get the latest
          const sorted = [...posts].sort((a, b) => 
            new Date(b.published_at || b.created_at) - new Date(a.published_at || a.created_at)
          );
          setPost(sorted[0]);
        }
      } catch (err) {
        console.error('Failed to fetch latest blog post:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLatestPost();
  }, []);

  if (loading || !post) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <section className="w-full py-16 sm:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mb-4">
            <FileText className="w-6 h-6 text-primary" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mb-2">
            Latest from our Blog
          </h2>
          <p className="text-muted-foreground">
            Insights, guides, and updates from GoCompareAI
          </p>
        </div>

        <Card className="overflow-hidden hover:shadow-lg transition-shadow">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Featured Image */}
            <div className="relative h-48 md:h-full min-h-[200px] bg-gradient-to-br from-primary/10 to-accent/10">
              {post.featured_image ? (
                <img 
                  src={post.featured_image} 
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <FileText className="w-16 h-16 text-primary/20" />
                </div>
              )}
            </div>

            {/* Content */}
            <CardContent className="p-6 flex flex-col justify-center">
              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {post.tags.slice(0, 3).map(tag => (
                    <span key={tag} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              
              <h3 className="text-xl font-semibold text-foreground mb-3 line-clamp-2">
                {post.title}
              </h3>
              
              <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                {post.excerpt || post.meta_description || 'Read our latest insights on AI platforms and tools.'}
              </p>

              <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(post.published_at || post.created_at)}
                </span>
                {post.reading_time && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {post.reading_time} min read
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Button asChild size="sm">
                  <Link to={`/blog/${post.slug}`}>
                    Read Article
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/blog">
                    View All Posts
                  </Link>
                </Button>
              </div>
            </CardContent>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default function LandingPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO pageType="home" />
      <OrganizationStructuredData />
      <Header />
      
      <main className="flex-1">
        {/* Wizard Section - Main Content */}
        <section className="w-full py-8 sm:py-12">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <WizardFlow />
          </div>
        </section>

        {/* Trust Statement - Clean and Simple */}
        <section className="w-full py-8 sm:py-12 bg-muted/30">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Scale className="w-5 h-5 text-primary" />
              <h3 className="font-medium text-foreground">Independent & transparent</h3>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              No paid rankings. Vendor information clearly labelled.
            </p>
            <p className="text-sm text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed">
              GoCompareAI is an independent AI comparison platform. We separate factual, 
              vendor-provided information from independent assessment so users can make 
              informed decisions with confidence.
            </p>
          </div>
        </section>

        {/* Category Browser - Browse by Category */}
        <CategoryBrowser />

        {/* Latest Blog Post Preview */}
        <LatestBlogPost />

        {/* Sustainability Section */}
        <section className="w-full py-16 sm:py-20 bg-muted/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-green-500/10 mb-6">
                <Leaf className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mb-4">
                Sustainability & Environmental Impact
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                AI platforms can choose to share environmental and sustainability information on GoCompareAI.
                When provided, these disclosures are supplied by the platform and help users consider sustainability alongside other factors.
              </p>
            </div>
            
            <Card className="border-green-200/50 bg-green-50/30 dark:bg-green-950/10">
              <CardContent className="p-6 sm:p-8">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                      <Info className="w-4 h-4 text-green-600" />
                      What you can find
                    </h3>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• Infrastructure providers and data centre regions</li>
                      <li>• Sustainability commitments and goals</li>
                      <li>• Carbon reporting availability</li>
                      <li>• Certifications and official reports</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      How we handle it
                    </h3>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• All information is vendor-provided</li>
                      <li>• Clearly labelled as vendor disclosures</li>
                      <li>• No sustainability rankings or scores</li>
                      <li>• GoCompareAI does not verify claims</li>
                    </ul>
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground mt-6 pt-4 border-t border-green-200/50">
                  Sustainability disclosures appear on individual platform pages when vendors have 
                  chosen to share this information. Look for the green leaf icon.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Vendor Section with New AI Chip Image */}
        <section className="w-full py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mb-4">
                  For AI providers
                </h2>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  GoCompareAI offers AI providers a neutral way to ensure their platform 
                  is accurately represented. Claimed and verified profiles allow vendors 
                  to maintain factual information and provide official documentation — 
                  without influencing rankings or outcomes.
                </p>
                
                <Link 
                  to="/vendors" 
                  className="text-sm text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1"
                >
                  Learn about vendor profiles
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              
              <div className="hidden lg:flex items-center justify-center">
                <div className="w-72 h-72 rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/f1ee4b6c-96b3-4697-9d10-cbc2be7ed836/images/491765c214f2d1f68b0aea0a1cb3b313b42d483b9d993fb66dabad046a016cd6.png"
                    alt="AI Comparison Technology"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
